import React, { FC, useEffect, useState } from "react";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import {
  Box,
  Stack,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Grow,
} from "@mui/material";
import { useAppDispatch, useAppSelector } from "../../store";
import {
  listenToMatchs,
  getPlayersWithCurrentMatchs,
} from "../../features/matchs/matchsSlice";
import { Players } from "../../types";

interface LivePlayerProps {}

const LivePlayer: FC<LivePlayerProps> = () => {
  const [maxHeight, setMaxHeight] = useState<number>(600);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const dispatch = useAppDispatch();

  // Données en temps réel depuis le store (mise à jour via onSnapshot)
  const playersWithCurrentMatchs: Players[] = useAppSelector(
    getPlayersWithCurrentMatchs
  );

  // On lance l'écoute temps réel à l'initialisation
  useEffect(() => {
    const unsubscribe = dispatch(listenToMatchs());
    return () => unsubscribe();
  }, [dispatch]);

  // Calcul dynamique de la hauteur max de la table
  useEffect(() => {
    const updateHeight = () => {
      const appBar = document.querySelector("nav.MuiAppBar-root");
      const appBarHeight = appBar?.getBoundingClientRect().height || 64;
      const padding = isMobile ? 140 : 110;
      setMaxHeight(window.innerHeight - appBarHeight - padding);
    };
    updateHeight();
    window.addEventListener("resize", updateHeight);
    return () => window.removeEventListener("resize", updateHeight);
  }, [isMobile]);

  const getMatchStart = (player: Players) =>
    player.matchPlayerParticipation?.[0]?.match?.dateLancement
      ? new Date(player.matchPlayerParticipation[0].match.dateLancement)
      : null;

  const getDuration = (start: Date | null) => {
    if (!start) return "-";
    const now = new Date();
    const minutes = Math.floor((now.getTime() - start.getTime()) / 60000);
    return `${minutes} min`;
  };

  const [sortField, setSortField] = useState<string | null>(null);
  const [sortAsc, setSortAsc] = useState<boolean>(true);

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortAsc(!sortAsc);
    } else {
      setSortField(field);
      setSortAsc(true);
    }
  };

  const sortedPlayers = [...(playersWithCurrentMatchs || [])].sort((a, b) => {
    if (!sortField) return 0;
    const getSortValue = (player: Players) => {
      const match = player.matchPlayerParticipation?.[0]?.match;
      switch (sortField) {
        case "name":
          return `${player.name} ${player.surname}`.toLowerCase();
        case "table":
          return match?.tableId ?? 0;
        case "heure": {
          const date = getMatchStart(player);
          return date ? date.getTime() : 0;
        }
        case "duree": {
          const date = getMatchStart(player);
          return date ? Date.now() - date.getTime() : 0;
        }
        default:
          return 0;
      }
    };

    const valA = getSortValue(a);
    const valB = getSortValue(b);
    if (valA < valB) return sortAsc ? -1 : 1;
    if (valA > valB) return sortAsc ? 1 : -1;
    return 0;
  });

  return (
    <Stack spacing={2} alignItems="center">
      <Box
        sx={{
          p: 2,
          borderRadius: 2,
          backgroundColor: theme.palette.primary.light,
          color: theme.palette.primary.contrastText,
          textAlign: "center",
          maxWidth: 1400,
        }}
      >
        <Typography variant="body1" fontWeight={500}>
          Veuillez trouver ici la liste des joueurs ayant un match en cours
        </Typography>
      </Box>
      <TableContainer
        component={Paper}
        sx={{
          maxWidth: 1400,
          maxHeight: `${maxHeight}px`,
          overflow: "auto",
          display: "block",
        }}
      >
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              {["name", "table", "heure", "duree"].map((field) => (
                <TableCell
                  key={field}
                  onClick={() => handleSort(field)}
                  sx={{
                    py: 0.5,
                    px: 1.5,
                    cursor: "pointer",
                    userSelect: "none",
                    fontWeight: 600,
                    fontSize: "0.875rem",
                    gap: 0.5,
                    "&:hover": {
                      backgroundColor: "action.hover",
                    },
                  }}
                >
                  {field === "name"
                    ? "Joueur"
                    : field === "table"
                    ? "Table"
                    : field === "heure"
                    ? "Heure"
                    : "Durée"}{" "}
                  {sortField === field ? (sortAsc ? "▲" : "▼") : ""}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedPlayers.map((player) => {
              const match = player.matchPlayerParticipation?.[0]?.match;
              const start = getMatchStart(player);
              return (
                <Grow in timeout={400} key={player.id}>
                  <TableRow
                    hover
                    sx={{
                      backgroundColor: (theme) =>
                        sortedPlayers.indexOf(player) % 2 === 0
                          ? theme.palette.secondary.light + "33" // clair pour lignes paires
                          : theme.palette.primary.light + "22", // plus chaud pour impaires
                      "&:hover": {
                        backgroundColor: theme.palette.primary.light + "55",
                      },
                    }}
                  >
                    <TableCell>
                      {player.name} {player.surname}
                    </TableCell>
                    <TableCell>{match?.tableId ?? "-"}</TableCell>
                    <TableCell>
                      {start
                        ? start.toLocaleTimeString("fr-FR", {
                            hour: "2-digit",
                            minute: "2-digit",
                          })
                        : "-"}
                    </TableCell>
                    <TableCell>{getDuration(start)}</TableCell>
                  </TableRow>
                </Grow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Stack>
  );
};

export default LivePlayer;
